

import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";

export default function TermsOfServicePage() {
  return (
    <LandingPageShell surface="white" className="text-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
            <span>Legal · Terms of Service</span>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm px-5 py-8 md:px-8 md:py-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Terms of Service
          </h1>
          <p className="text-xs md:text-sm text-slate-500 mb-8">
            Last updated: October 2024
          </p>

          <div className="prose prose-slate max-w-none">
            <p>
              These Terms of Service (&quot;Terms&quot;) govern your access to and
              use of <strong>inaraX</strong> (the &quot;Service&quot;), an AI-Native
              Learning Operating System operated by atomcamp Arabia
              (&quot;atomcamp&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;). By
              using the Service, you agree to be bound by these Terms. Please
              read them carefully.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              1. Description of Service
            </h2>
            <p>
              inaraX, operated by atomcamp Arabia, is an AI-native educational
              platform. Unlike traditional course catalogs, inaraX utilizes a
              proprietary &quot;Two Brains&quot; architecture (Fast Brain for routing
              and Deep Brain for generative tutoring) to provide a personalized,
              adaptive learning experience.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              2. Eligibility and Account Security
            </h2>
            <p>
              <strong>Age.</strong> You must be at least 13 years old to use the
              Service. Users under 18 require parental or guardian consent.
            </p>
            <p>
              <strong>Account.</strong> You are responsible for maintaining the
              confidentiality of your login credentials. Any activity occurring
              under your account is your sole responsibility.
            </p>
            <p>
              <strong>Verification.</strong> You agree to provide accurate,
              current, and complete information during the registration and
              placement process.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              3. The &quot;Learning OS&quot; &amp; AI Generation
            </h2>
            <p>
              <strong>Adaptive paths.</strong> You acknowledge that inaraX uses
              AI to determine your learning path. This path is dynamic and based
              on your placement signals and quiz performance.
            </p>
            <p>
              <strong>Accuracy.</strong> While our Deep Brain is constrained to
              verified expert content via retrieval-augmented generation (RAG)
              to reduce hallucinations, AI-generated content is provided for
              educational purposes. atomcamp Arabia strives for professional
              accuracy but does not warrant that every AI-generated analogy or
              drill is free from technical nuances.
            </p>
            <p>
              <strong>System integrity.</strong> You agree not to attempt to
              jailbreak, manipulate, or reverse-engineer the AI prompts or
              underlying architecture.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              4. Credentialing &amp; Living Certifications
            </h2>
            <p>
              <strong>Verification.</strong> Credentials are only issued upon
              successful completion of the Verified Path as determined by the
              OS and human-expert oversight.
            </p>
            <p>
              <strong>Renewal cycle.</strong> inaraX certifications are Living
              Credentials. To maintain professional relevance in the rapidly
              evolving AI landscape, most certifications expire every six (6)
              months. Renewal requires passing a delta-assessment to prove
              updated mastery.
            </p>
            <p>
              <strong>No-cheat protocol.</strong> Our Service utilizes
              proprietary frontend technology to help ensure academic integrity.
              Attempting to bypass these measures (for example, tab-switching,
              unauthorized copy-pasting, or using external AI assistants during
              assessments) may result in immediate revocation of credentials and
              account suspension.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              5. Intellectual Property
            </h2>
            <p>
              <strong>Ownership.</strong> All software, AI models, content,
              &quot;Two Brains&quot; architecture, and branding are the exclusive
              property of atomcamp Arabia or its licensors.
            </p>
            <p>
              <strong>License.</strong> We grant you a limited,
              non-transferable, revocable license to access the content for
              personal or internal professional development. You may not scrape,
              download for distribution, or resell any part of the inaraX
              platform.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              6. Payments, Subscriptions, and Refunds
            </h2>
            <p>
              <strong>Subscription.</strong> Access to certain features (Pro and
              Enterprise) requires a paid subscription.
            </p>
            <p>
              <strong>Billing.</strong> Subscriptions auto-renew unless
              cancelled at least 24 hours before the end of the current period.
            </p>
            <p>
              <strong>Refunds.</strong> Due to the generative nature of the
              content and immediate resource allocation of our AI models,
              refunds are generally not provided after the initial 7-day trial
              period unless required by applicable KSA consumer law.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              7. Prohibited Conduct
            </h2>
            <p>You agree not to:</p>
            <ul>
              <li>
                Use the Service for any illegal purpose or in violation of Saudi
                Arabian laws.
              </li>
              <li>
                Engage in data mining or use automated systems to extract data
                from the OS.
              </li>
              <li>Share your account with multiple users.</li>
              <li>
                Infringe upon the intellectual property rights of atomcamp
                Arabia or its human experts.
              </li>
            </ul>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              8. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, atomcamp Arabia shall not
              be liable for any indirect, incidental, or consequential damages
              resulting from your use of the Service or the performance of the
              AI models.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              9. Governing Law
            </h2>
            <p>
              These Terms are governed by and construed in accordance with the
              laws of the Kingdom of Saudi Arabia. Any disputes arising from
              these Terms shall be subject to the exclusive jurisdiction of the
              courts in Riyadh, KSA.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              10. Modifications to Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. We will
              notify users of significant changes via email or platform
              notifications. Continued use of the Service after changes
              constitutes acceptance of the new Terms.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              11. Contact Information
            </h2>
            <p>
              For questions regarding these Terms, please contact us at:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:team@atomcamparabia.com">team@atomcamparabia.com</a>
              <br />
              <strong>Address:</strong> atomcamp Arabia, Riyadh, Kingdom of
              Saudi Arabia
            </p>
          </div>
        </div>
      </div>
    </LandingPageShell>
  );
}

